import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { SignupWizardServerService } from '../../services/signup-wizard-server.service';

type PaymentOutcome = 'SUCCESS' | 'FAILED' | 'PENDING';

@Component({
    selector: 'app-order-success',
    templateUrl: './order-success.component.html',
    styleUrls: ['./order-success.component.scss'],
    standalone: false
})
export class OrderSuccessComponent implements OnInit, OnDestroy {
  subscriptionInfo: any; // This will hold the subscription details
  subscriptionId: string | null = null; // Subscription ID from query params
  loading: boolean = true;
  errorMessage: string | null = null;

  // Real payment outcome reported by the backend. The customer is redirected
  // here after Cashfree regardless of success/failure, so we must branch on
  // this rather than assume success.
  paymentOutcome: PaymentOutcome | null = null;
  failureReason: string | null = null;
  retryUserId: string | null = null;

  // The success webhook is async, so an immediate landing can legitimately read
  // PENDING for a few seconds. Poll a bounded number of times before settling.
  private pollAttempts = 0;
  private readonly maxPollAttempts = 5;
  private readonly pollIntervalMs = 4000;
  private pollTimer: any = null;

  constructor(
    private route: ActivatedRoute,
    private signupwizardService: SignupWizardServerService,
    private location: Location,
    private router: Router
  ) {}

  ngOnInit(): void {
    const qp = this.route.snapshot.queryParams;
    this.subscriptionId = qp.subscription_id;

    if (!this.subscriptionId) {
      this.errorMessage = 'Subscription ID is missing.';
      this.loading = false;
      return;
    }

    // Cashfree puts the outcome straight in the return URL (e.g.
    // cf_checkoutStatus=FAILED / cf_status=ERROR / cf_message=...). Trust a
    // FAILURE signal immediately — show the failure screen without the
    // "Confirming…" flash or polling, and forward the reason so the backend
    // records the lead + emails the exact cause. SUCCESS still needs backend
    // confirmation (never trust the URL for that).
    const urlFailed =
      qp.cf_checkoutStatus === 'FAILED' ||
      qp.cf_status === 'FAILED' ||
      qp.cf_status === 'ERROR';
    const urlReason: string | null = qp.cf_message || null;

    if (urlFailed) {
      this.paymentOutcome = 'FAILED';
      this.failureReason = urlReason;
    }

    this.fetchSubscriptionDetails(this.subscriptionId, false, urlReason);
  }

  ngOnDestroy(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
    }
  }

  // Fetch subscription details by subscriptionId
  fetchSubscriptionDetails(
    subscriptionId: string,
    isPoll = false,
    failureReason: string | null = null
  ): void {
    if (!isPoll) {
      this.loading = true;
    }
    this.signupwizardService
      .getSubscriptionDetails(subscriptionId, failureReason)
      .subscribe(
      (response: any) => {
        // Backward compatible: if an older backend omits paymentOutcome, the
        // mere presence of subscription data implies success.
        const backendOutcome =
          response?.paymentOutcome ?? (response?.data ? 'SUCCESS' : null);
        // A URL-confirmed failure wins over a lagging PENDING backend read.
        this.paymentOutcome =
          this.paymentOutcome === 'FAILED' ? 'FAILED' : backendOutcome;
        this.failureReason = response?.failureReason ?? this.failureReason;
        this.retryUserId = response?.retryUserId ?? this.retryUserId;

        if (response && response.data) {
          this.subscriptionInfo = response.data;
        }

        // Still settling — poll a few more times before showing a verdict.
        if (
          this.paymentOutcome === 'PENDING' &&
          this.pollAttempts < this.maxPollAttempts
        ) {
          this.pollAttempts++;
          this.loading = false; // show the "confirming" screen meanwhile
          this.pollTimer = setTimeout(
            () => this.fetchSubscriptionDetails(subscriptionId, true, failureReason),
            this.pollIntervalMs
          );
          return;
        }

        if (this.paymentOutcome === 'SUCCESS' && !this.subscriptionInfo) {
          this.errorMessage = 'Subscription details not found.';
        }
        this.loading = false;
      },
      (error) => {
        this.errorMessage =
          'Failed to load subscription details. Please try again later.';
        this.loading = false;
      }
    );
  }

  // Re-run the lookup on demand (e.g. customer clicks "Check again" on the
  // pending screen).
  checkAgain(): void {
    if (!this.subscriptionId) {
      return;
    }
    this.pollAttempts = 0;
    this.fetchSubscriptionDetails(this.subscriptionId);
  }

  // Send the customer back into the wizard to re-attempt payment. Passing the
  // userId triggers the resume-lead flow (skips straight to OTP -> plan -> pay).
  retryPayment(): void {
    const queryParams = this.retryUserId ? { userId: this.retryUserId } : {};
    this.router.navigate(['/authentication/sign-up'], { queryParams });
  }

  // Convert plan type id to a human-readable text
  getPlanTypeText(planTypeId: number): string {
    switch (planTypeId) {
      case 1:
        return 'Monthly';
      case 2:
        return 'Quarterly';
      case 3:
        return 'Yearly';
      default:
        return 'Unknown';
    }
  }

  // Go back to the previous page
  goBack(): void {
    this.location.back();
  }

  // Trigger browser print dialog so the customer can save/print a receipt
  printPage(): void {
    window.print();
  }
}
